import { formatFileSize } from '@anchorage/utils';
import { IconButton } from '@latitude/icon-button';
import { Icon } from '@latitude/icons';
import { Stack } from '@latitude/layout';
import { ProgressBar } from '@latitude/progress-bar';
import { Text } from '@latitude/text';
import type { UploadedFile } from 'types';

type FileListProps = {
  files: UploadedFile[];
  onClear?: (
    index: number,
  ) => (event: React.MouseEvent<HTMLButtonElement>) => void;
};

export const FileList = ({ files, onClear }: FileListProps) => {
  if (files.length === 0) {
    return null;
  }

  return (
    <Stack
      as="ul"
      gap={8}
      className="mt-spacing-sys-spacing-8"
      data-testid="file-list"
    >
      {files.map((fileObj, index) => {
        const fileHasProgress = fileObj.progress !== undefined;
        const fileHasError = fileObj.errorMessage !== undefined;

        const fileSizeRaw = fileObj.file?.size ?? 0;
        const fileSizeFormatted = formatFileSize(fileSizeRaw);
        const currentUploadedPercentage = fileHasProgress
          ? (fileSizeRaw * (fileObj.progress ?? 0)) / 100
          : 0;
        const uploadedfileSizeFormatted = formatFileSize(
          currentUploadedPercentage,
        );

        return (
          <Stack as="li" key={fileObj?.name || index} gap={4}>
            <Stack
              direction="horizontal"
              align="center"
              justify="between"
              gap={8}
            >
              <Stack gap={4}>
                {fileObj?.url ? (
                  <Text asChild variant="link" color="strong">
                    <a
                      href={fileObj.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {fileObj.name}
                    </a>
                  </Text>
                ) : (
                  <Text color="strong">{fileObj.name}</Text>
                )}

                {fileHasProgress && !fileHasError && (
                  <Stack direction="horizontal" align="center" gap={4}>
                    {fileObj.progress === 100 ? (
                      <Text
                        color="success"
                        className="flex items-center gap-[4px]"
                      >
                        {`File uploaded: ${fileSizeFormatted}`}
                        {fileObj.progress === 100 && (
                          <Icon name="check" height="16px" width="16px" />
                        )}
                      </Text>
                    ) : (
                      <>
                        <Text color="info">
                          {`Uploading file: ${uploadedfileSizeFormatted}`}
                        </Text>
                        <Text color="info" className="ml-[18px]">
                          {fileSizeFormatted}
                        </Text>
                      </>
                    )}
                  </Stack>
                )}
                {fileHasError && (
                  <Text color="danger">{fileObj.errorMessage}</Text>
                )}
              </Stack>
              {onClear && (
                <IconButton
                  htmlType="button"
                  onClick={onClear(index)}
                  variant="strong"
                  type="ghost"
                  size="small"
                  icon={<Icon name="x-close" />}
                  className="self-start"
                  data-testid="remove-file-btn"
                  aria-label="Remove file"
                />
              )}
            </Stack>
            {fileHasProgress && !fileHasError && fileObj.progress !== 100 ? (
              <ProgressBar
                size="small"
                value={fileObj.progress ?? 0}
                status="primary"
              />
            ) : null}
          </Stack>
        );
      })}
    </Stack>
  );
};
